// frontend/src/components/widgets/WeatherWidgetConfig.js - SIMPLIFIED WITH PRESETS
import React, { useState, useEffect } from 'react';
import { Cloud, MapPin, Eye, Clock, Save, X, Layout, Palette } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import WeatherWidget, { DISPLAY_PRESETS } from './WeatherWidget';

const WeatherWidgetConfig = ({ onSave, onCancel, existingConfig = null }) => {
  const { t } = useTranslation();

  // 🔍 DEBUG: Was bekommen wir als existingConfig?
  console.log('🌦️ WeatherWidgetConfig received existingConfig:', existingConfig);
  
  const [config, setConfig] = useState({
    name: '',
    location: '',
    units: 'metric',
    showForecast: true,
    updateInterval: 600,
    layout: 'cards',
    theme: 'dynamic',
    displayPreset: 'standard' // NEW: Default preset
  });

  const [previewWeather, setPreviewWeather] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (existingConfig) {
      console.log('📝 Editing existing config:', existingConfig);
      setConfig({
        name: existingConfig.name || '',
        location: existingConfig.config?.location || '',
        units: existingConfig.config?.units || 'metric',
        showForecast: existingConfig.config?.showForecast ?? true,
        updateInterval: existingConfig.config?.updateInterval || 600,
        layout: existingConfig.config?.layout || 'cards',
        theme: existingConfig.config?.theme || 'dynamic',
        // Backward compatibility: if displayOptions exist, determine preset
        displayPreset: existingConfig.config?.displayPreset || determinePresetFromOptions(existingConfig.config?.displayOptions) || 'standard'
      });
    }
  }, [existingConfig]);

  // Helper: Determine preset from displayOptions (for backward compatibility)
  const determinePresetFromOptions = (displayOptions) => {
    if (!displayOptions) return 'standard';
    
    // Check which preset matches the displayOptions
    for (const [presetKey, presetData] of Object.entries(DISPLAY_PRESETS)) {
      const match = Object.entries(presetData.options).every(
        ([key, value]) => displayOptions[key] === value
      );
      if (match) return presetKey;
    }
    
    return 'standard'; // Default fallback
  };

  // ✅ Layout Options
  const layoutOptions = [
    { 
      value: 'cards', 
      label: 'Card-Based Layout',
      description: 'Moderne Glassmorphism-Cards mit Details'
    },
    { 
      value: 'dashboard', 
      label: 'Dashboard Layout',
      description: 'Split-Layout mit 5-Tage Vorhersage'
    }
  ];

  // ✅ Enhanced Theme Options with better descriptions
  const themeOptions = [
    { 
      value: 'dynamic', 
      label: '🌈 Dynamic',
      description: 'Wetterbasierter Hintergrund (empfohlen)'
    },
    { 
      value: 'modern', 
      label: '💜 Modern',
      description: 'Eleganter Lila/Pink Gradient'
    },
    { 
      value: 'classic', 
      label: '🔵 Classic',
      description: 'Klassischer Blau-Gradient'
    },
    { 
      value: 'weather', 
      label: '🌊 Weather',
      description: 'Wettertypische Blau-Töne'
    },
    { 
      value: 'minimal', 
      label: '⚪ Minimal',
      description: 'Heller Hintergrund'
    },
    { 
      value: 'dark', 
      label: '⚫ Dark',
      description: 'Dunkler Hintergrund'
    }
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!config.name.trim()) {
      newErrors.name = t('weatherConfig.validation.nameRequired');
    }

    if (!config.location.trim()) {
      newErrors.location = t('weatherConfig.validation.locationRequired');
    }

    if (config.updateInterval < 300) {
      newErrors.updateInterval = t('weatherConfig.validation.intervalMin');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLocationSearch = async () => {
    if (!config.location.trim()) return;

    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setPreviewWeather({
        location: config.location,
        temperature: 22,
        feelsLike: 20,
        condition: 'Partly Cloudy',
        humidity: 65,
        windSpeed: 12,
        pressure: 1013,
        visibility: 10
      });
    } catch (error) {
      console.error('Error fetching weather:', error);
      setErrors({ ...errors, location: t('weatherConfig.validation.locationFetchError') });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = () => {
    if (validateForm()) {
      console.log('💾 Saving widget with config:', config);
      
      const widgetData = {
        name: config.name,
        config: {
          location: config.location,
          units: config.units,
          showForecast: config.showForecast,
          updateInterval: config.updateInterval,
          layout: config.layout,
          theme: config.theme,
          displayPreset: config.displayPreset // Save preset instead of individual options
        }
      };
      
      console.log('📤 Sending to parent:', widgetData);
      onSave(widgetData);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-lg">
            <Cloud size={24} className="text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
              {existingConfig ? 'Wetter-Widget bearbeiten' : 'Wetter-Widget Konfiguration'}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Richten Sie Ihre Wetteranzeige ein
            </p>
          </div>
        </div>
        <button
          onClick={onCancel}
          className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
        >
          <X size={24} />
        </button>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
        <div className="p-6 space-y-6">
          
          {/* Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            {/* LEFT COLUMN: Configuration */}
            <div className="space-y-6">
              
              {/* Basic Settings */}
              <div>
                <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                  <MapPin size={20} />
                  Grundeinstellungen
                </h4>

                {/* Widget Name */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Widget-Name *
                  </label>
                  <input
                    type="text"
                    value={config.name}
                    onChange={(e) => setConfig({ ...config, name: e.target.value })}
                    className={`w-full px-4 py-2 border ${errors.name ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100`}
                    placeholder="z.B. Lobby Wetteranzeige"
                  />
                  {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                </div>

                {/* Location */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Standort *
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={config.location}
                      onChange={(e) => setConfig({ ...config, location: e.target.value })}
                      className={`flex-1 px-4 py-2 border ${errors.location ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100`}
                      placeholder="z.B. Zürich, Schweiz"
                    />
                    <button
                      type="button"
                      onClick={handleLocationSearch}
                      disabled={loading}
                      className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                      {loading ? 'Testen...' : 'Testen'}
                    </button>
                  </div>
                  {errors.location && <p className="text-red-500 text-sm mt-1">{errors.location}</p>}
                </div>

                {/* Units */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Temperatureinheiten
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        value="metric"
                        checked={config.units === 'metric'}
                        onChange={(e) => setConfig({ ...config, units: e.target.value })}
                        className="text-blue-600"
                      />
                      <span className="text-gray-700 dark:text-gray-300">Metrisch (°C, km/h)</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        value="imperial"
                        checked={config.units === 'imperial'}
                        onChange={(e) => setConfig({ ...config, units: e.target.value })}
                        className="text-blue-600"
                      />
                      <span className="text-gray-700 dark:text-gray-300">Imperial (°F, mph)</span>
                    </label>
                  </div>
                </div>

                {/* Update Interval */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Aktualisierungsintervall (Sekunden)
                  </label>
                  <input
                    type="number"
                    value={config.updateInterval}
                    onChange={(e) => setConfig({ ...config, updateInterval: parseInt(e.target.value) })}
                    min="300"
                    step="60"
                    className={`w-full px-4 py-2 border ${errors.updateInterval ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100`}
                  />
                  {errors.updateInterval && <p className="text-red-500 text-sm mt-1">{errors.updateInterval}</p>}
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Minimum: 300 Sekunden (5 Minuten)</p>
                </div>

                {/* Show Forecast Toggle */}
                <div className="mb-4">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={config.showForecast}
                      onChange={(e) => setConfig({ ...config, showForecast: e.target.checked })}
                      className="w-5 h-5 text-blue-600 rounded"
                    />
                    <span className="text-gray-700 dark:text-gray-300">
                      5-Tage Vorhersage anzeigen
                    </span>
                  </label>
                </div>
              </div>

              {/* ✅ Layout Selection */}
              <div>
                <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                  <Layout size={20} />
                  Layout
                </h4>
                <div className="space-y-3">
                  {layoutOptions.map(option => (
                    <label 
                      key={option.value}
                      className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        config.layout === option.value
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 shadow-md'
                          : 'border-gray-300 dark:border-gray-600 hover:border-blue-300 hover:shadow-sm'
                      }`}
                    >
                      <input
                        type="radio"
                        value={option.value}
                        checked={config.layout === option.value}
                        onChange={(e) => setConfig({ ...config, layout: e.target.value })}
                        className="mt-1 text-blue-600"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 dark:text-gray-100">{option.label}</div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">{option.description}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* ✅ Enhanced Theme Selection */}
              <div>
                <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                  <Palette size={20} />
                  Design-Theme
                </h4>
                <div className="space-y-2">
                  {themeOptions.map(option => (
                    <label 
                      key={option.value}
                      className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                        config.theme === option.value
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 shadow-sm'
                          : 'border-gray-200 dark:border-gray-700 hover:border-blue-300'
                      }`}
                    >
                      <input
                        type="radio"
                        value={option.value}
                        checked={config.theme === option.value}
                        onChange={(e) => setConfig({ ...config, theme: e.target.value })}
                        className="text-blue-600"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 dark:text-gray-100">
                          {option.label}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          {option.description}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* 🎯 NEW: Display Preset Dropdown */}
              <div>
                <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                  <Eye size={20} />
                  Anzeigeoptionen
                </h4>
                <div className="mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Informationsdichte
                  </label>
                  <select
                    value={config.displayPreset}
                    onChange={(e) => setConfig({ ...config, displayPreset: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                  >
                    {Object.entries(DISPLAY_PRESETS).map(([key, preset]) => (
                      <option key={key} value={key}>
                        {t(preset.label)}
                      </option>
                    ))}
                  </select>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                    {t(DISPLAY_PRESETS[config.displayPreset].description)}
                  </p>
                </div>

                {/* Preview what the preset includes */}
                <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700">
                  <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Enthaltene Informationen:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(DISPLAY_PRESETS[config.displayPreset].options)
                      .filter(([_, value]) => value)
                      .map(([key, _]) => (
                        <span 
                          key={key} 
                          className="text-xs px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full"
                        >
                          {getDisplayOptionLabel(key)}
                        </span>
                      ))}
                  </div>
                </div>
              </div>

            </div>

            {/* RIGHT COLUMN: Optimized Live Preview */}
            <div className="sticky top-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Live-Vorschau
                </h4>
                <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded-full">
                  Echtzeit-Update
                </span>
              </div>
              
              {/* Optimized Preview Container - Responsive & No Scrollbar */}
              <div 
                className="bg-gray-100 dark:bg-gray-900 rounded-xl overflow-hidden shadow-xl border border-gray-200 dark:border-gray-700 relative" 
              >
                {/* Responsive Scaled Preview */}
                <div className="hidden xl:block" style={{ height: '500px' }}>
                  <div 
                    style={{
                      transform: 'scale(0.4)',
                      transformOrigin: 'top left',
                      width: '250%',
                      height: '250%',
                      overflow: 'hidden'
                    }}
                  >
                    <WeatherWidget 
                      config={config} 
                      screenSize={{
                        breakpoint: 'FHD',
                        fontSizeMultiplier: 1.0,
                        spacingMultiplier: 1.0
                      }}
                    />
                  </div>
                </div>
                
                {/* Large screens (lg to xl) */}
                <div className="hidden lg:block xl:hidden" style={{ height: '400px' }}>
                  <div 
                    style={{
                      transform: 'scale(0.32)',
                      transformOrigin: 'top left',
                      width: '312.5%',
                      height: '312.5%',
                      overflow: 'hidden'
                    }}
                  >
                    <WeatherWidget 
                      config={config} 
                      screenSize={{
                        breakpoint: 'HD',
                        fontSizeMultiplier: 1.0,
                        spacingMultiplier: 1.0
                      }}
                    />
                  </div>
                </div>
                
                {/* Mobile/Tablet (below lg) */}
                <div className="block lg:hidden" style={{ height: '350px' }}>
                  <div 
                    style={{
                      transform: 'scale(0.28)',
                      transformOrigin: 'top left',
                      width: '357%',
                      height: '357%',
                      overflow: 'hidden'
                    }}
                  >
                    <WeatherWidget 
                      config={config} 
                      screenSize={{
                        breakpoint: 'HD',
                        fontSizeMultiplier: 1.0,
                        spacingMultiplier: 1.0
                      }}
                    />
                  </div>
                </div>
              </div>
              
              {/* Preview Info */}
              <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <p className="text-xs text-blue-800 dark:text-blue-200">
                  💡 <strong>Tipp:</strong> Die Vorschau zeigt Ihr Widget in Echtzeit!
                </p>
              </div>
            </div>

          </div>

        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-end gap-4 p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors font-medium"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 font-medium shadow-lg hover:shadow-xl"
          >
            <Save size={20} />
            {existingConfig ? 'Aktualisieren' : 'Speichern'}
          </button>
        </div>
      </form>
    </div>
  );

  // Helper function for display option labels
  function getDisplayOptionLabel(key) {
    const labels = {
      showTemperature: 'Temperatur',
      showFeelsLike: 'Gefühlt',
      showHumidity: 'Luftfeuchtigkeit',
      showWind: 'Wind',
      showPressure: 'Luftdruck',
      showVisibility: 'Sichtweite',
      animatedIcons: 'Animierte Icons'
    };
    return labels[key] || key;
  }
};

export default WeatherWidgetConfig;